import React, { useEffect, useState, useRef } from "react";
import { gsap } from "gsap";
import { Flip } from "gsap/Flip";
import classNames from "classnames";
import styles from "./Services.module.scss";
import { useGSAP } from "@gsap/react";

gsap.registerPlugin(Flip);

const Services = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const tabLayoutContainer = useRef(null);
  const imageContainer = useRef(null);
  const typeOfServiceTitle = useRef(null);

  useGSAP(() => {
    gsap.from(tabLayoutContainer.current.children, {
      x: 100, // move the items up
      y: 160,
      opacity: 0, // start with 0 opacity
      stagger: 0.5, // stagger the appearance of each item
      ease: "power2.out",
      duration: 1,
      delay: 0.5,
      scrollTrigger: {
        trigger: tabLayoutContainer.current,
        start: "top+=100vh center",
        toggleActions: "play none none none", // only play the animation once
      },
    });
    gsap.from(imageContainer.current.children, {
      x: 100, // move the items up
      y: 160,
      opacity: 0, // start with 0 opacity
      stagger: 0.5, // stagger the appearance of each item
      ease: "power2.out",
      duration: 1,
      delay: 1.3,
      scrollTrigger: {
        trigger: imageContainer.current,
        start: "top+=100vh center",
        toggleActions: "play none none none", // only play the animation once
      },
    });
    gsap.from(typeOfServiceTitle.current, {
      y: -60,
      opacity: 0, // start with 0 opacity
      ease: "power2.out",
      duration: 1,
      scrollTrigger: {
        trigger: typeOfServiceTitle.current,
        start: "top+=100vh center",
        toggleActions: "play none none none", // only play the animation once
      },
    });
  });

  useEffect(() => {
    const initFlipButtons = () => {
      const wrappers = document.querySelectorAll(`[data-flip-button="wrap"]`);

      wrappers.forEach((wrapper) => {
        const buttons = wrapper.querySelectorAll(`[data-flip-button="button"]`);
        const bg = wrapper.querySelector(`[data-flip-button="bg"]`);

        buttons.forEach((button) => {
          const handleEnter = () => {
            const state = Flip.getState(bg);
            button.appendChild(bg);
            Flip.from(state, { duration: 0.4 });
          };

          const handleLeave = () => {
            const state = Flip.getState(bg);
            const activeButton = wrapper.querySelector(`.${styles.active}`);
            activeButton?.appendChild(bg);
            Flip.from(state, { duration: 0.4 });
          };

          button.addEventListener("mouseenter", handleEnter);
          button.addEventListener("mouseleave", handleLeave);
          button.addEventListener("focus", handleEnter);
          button.addEventListener("blur", handleLeave);
        });
      });
    };

    const initTabSystem = () => {
      const wrappers = document.querySelectorAll(`[data-tabs="wrapper"]`);

      wrappers.forEach((wrapper) => {
        const buttons = wrapper.querySelectorAll(`[data-tabs="button"]`);
        const contentItems = wrapper.querySelectorAll(
          `[data-tabs="content-item"]`
        );
        const visualItems = wrapper.querySelectorAll(
          `[data-tabs="visual-item"]`
        );

        buttons.forEach((button, i) => {
          button.addEventListener("click", () => setActiveIndex(i));
        });

        gsap.set(contentItems, { autoAlpha: 0 });
        gsap.set(visualItems, { autoAlpha: 0 });

        gsap.to(contentItems[activeIndex], { autoAlpha: 1 });
        gsap.to(visualItems[activeIndex], { autoAlpha: 1 });
      });
    };

    initFlipButtons();
    initTabSystem();
  }, [activeIndex]);

  const tabs = [
    {
      title: "ESG / Sustainable Finance",
      heading: "Sustainable Roles",
      description:
        "We partner with leading consultancies, financial institutions, and data providers to recruit for roles in ESG analysis, climate risk, CSR, sustainable finance, responsible investing, and regulatory reporting.",
      image:
        "https://www.monstertreeservice.com/images/articles/GettyImages-476116580.jpg",
    },
    {
      title: "Investment Banking & Private Equity",
      heading: "Building Teams",
      description:
        "We work with globally recognised banks, boutique advisors, and mid-sized firms to build teams in M&A, Debt & Equity Capital Markets, Restructuring, Valuation, Leveraged Financing and Buyouts.",
      image:
        "https://costar.brightspotcdn.com/dims4/default/696caaa/2147483647/strip/true/crop/2048x1356+0+0/resize/2048x1356!/quality/100/?url=http%3A%2F%2Fcostar-brightspot.s3.us-east-1.amazonaws.com%2FGettyImages-823866336.jpg",
    },
    {
      title: "Credit & Structured Finance",
      heading: "Credit Experts",
      description:
        "We specialise in recruiting for Credit and Structured Finance roles across global institutions and advisory firms. Our expertise includes Credit Research and Analysis for Financial Institutions, Corporates, Project Finance, Real Estate, and Leveraged Finance. We also focus on Structured Finance roles, including ABS, CLOs, RMBS, CMBS, NPLs, and Securitisation",
      image:
        "https://static.vecteezy.com/system/resources/previews/008/700/984/non_2x/business-accounting-team-meeting-in-room-office-using-pen-pointing-at-paperwork-finance-free-photo.jpg",
    },
    {
      title: "Asset & Investment Management",
      heading: "Buy-Side Insights",
      description:
        "We support asset managers, hedge funds, and private banks in hiring for Portfolio Management, Fund Analysis, Asset Allocation, ESG Integration, Investor Relations, Wealth Management, and Performance Reporting roles.",
      image:
        "https://img.freepik.com/premium-photo/stock-market-graph-dark-led-screen-business-analysis-finance-economic-digital-graphs_1254594-1787.jpg",
    },
    {
      title: "Financial Risk & Quantitative Analysis",
      heading: "Risk Specialists",
      description:
        "We assist top-tier financial institutions with recruitment for Risk and Quantitative positions, including Market Risk, Credit Risk, Liquidity Risk, Operational Risk, ALM, Risk Modelling, Stress Testing, and Quantitative Research.",
      image:
        "https://asset.providentestate.com/x/1773x/difc_banner_92502c8d84.webp",
    },
    {
      title: "Debt & Equity Capital Markets",
      heading: "Capital Markets Hiring",
      description:
        "We collaborate with leading investment banks, private equity firms, and boutique advisors to recruit for roles in Debt Issuance, High-Yield Bonds, Leveraged Loans, IPOs, Syndications, Convertible Bonds, and Structured Equity Solutions.",
      image:
        "https://www.ey.com/content/dam/ey-unified-site/ey-com/en-us/insights/private-business/images/ey-digital-stock-market-on-a-tablet-screen-.jpg",
    },
  ];

  return (
    <section className={styles.cloneable}>
      <div data-tabs="wrapper" className={styles.grid}>
        <h1 className={styles.tabLayoutHeading} ref={typeOfServiceTitle}>
          Types of Service
        </h1>
        <div className={styles.tabLayoutCol}>
          <div className={styles.tabLayoutContainer} ref={tabLayoutContainer}>
            <div className={styles.tabContainerTop}>
              <div data-flip-button="wrap" className={styles.filterBar}>
                {tabs.map((tab, index) => (
                  <button
                    key={index}
                    data-tabs="button"
                    data-flip-button="button"
                    className={classNames(styles.filterButton, {
                      [styles.active]: index === activeIndex,
                    })}
                    onClick={() => setActiveIndex(index)}
                  >
                    <div className={styles.filterButtonP}>{tab.title}</div>
                    <div
                      data-flip-button="bg"
                      className={styles.tabButtonBg}
                    ></div>
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.tabContainerBottom}>
              <div className={styles.tabContentWrap}>
                {tabs.map((tab, index) => (
                  <div
                    key={index}
                    data-tabs="content-item"
                    className={classNames(styles.tabContentItem, {
                      [styles.active]: index === activeIndex,
                    })}
                  >
                    <h2 className={styles.tabContentHeading}>{tab.heading}</h2>
                    <p className={styles.contentP}>{tab.description}</p>
                  </div>
                ))}
              </div>
              <a href="#" className={styles.tabContentButton}>
                <p className={styles.contentP}>Contact Us</p>
              </a>
            </div>
          </div>
        </div>

        <div className={styles.tabLayoutColPhoto} ref={imageContainer}>
          <div data-tabs="visual-wrap" className={styles.tabVisualWrap}>
            {tabs.map((tab, index) => (
              <div
                key={index}
                data-tabs="visual-item"
                className={classNames(styles.tabVisualItem, {
                  [styles.active]: index === activeIndex,
                })}
              >
                <img
                  src={tab.image}
                  alt={tab.title}
                  className={styles.tabImage}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
